import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Offcanvas } from 'react-bootstrap';
import authService from '../../services/authService';
import LoadingOverlay from '../../components/loading/loading';
import 'bootstrap/dist/css/bootstrap.min.css';
import './DashboardLayout.css';

const DashboardLayout = ({ children }) => {
    const push = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [userDetails, setUserDetails] = useState([]);

    useEffect(() => {
        getUserDetailsByID();
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            setSidebarOpen(!mobile); // Auto-hide for mobile, show for desktop
        };

        handleResize(); // Ensure initial render behaves correctly
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const getUserDetailsByID = async () => {
        setLoading(true);
        try {
            const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
            if (isLoggedIn) {
                const userId = sessionStorage.getItem('userId');
                const userDetail = await authService.getUserDetailsById(userId);
                if (!userDetail.success) {
                    toast.error(userDetail.message);
                    return;
                }
                setUserDetails(userDetail.user);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        try {
            setLoading(true);
            const result = authService.logout();
            if (result.success) {
                toast.success(result.message);
                push('/');
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const getInitials = () => {
        if (userDetails && userDetails.firstName && userDetails.lastName) {
            const firstNameInitial = userDetails.firstName.charAt(0).toUpperCase();
            const lastNameInitial = userDetails.lastName.charAt(0).toUpperCase();
            return `${firstNameInitial}${lastNameInitial}`;
        }
        return '';
    };

    const SidebarLinks = () => (
        <nav className="d-flex flex-column">
            <Link
                to="/admin/dashboard"
                className={`custom-button nav-link ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
            >
                🏠 Home
            </Link>
            <Link
                to="/admin/users"
                className={`custom-button nav-link ${location.pathname === '/admin/users' ? 'active' : ''}`}
            >
                👥 Users
            </Link>
            <Link
                to="/admin/hotels"
                className={`custom-button nav-link ${location.pathname === '/admin/hotels' ? 'active' : ''}`}
            >
                🏨 Hotels
            </Link>
            <Link
                to="/admin/events"
                className={`custom-button nav-link ${location.pathname === '/admin/events' ? 'active' : ''}`}
            >
                🎟️ Events
            </Link>
            <Link
                to="/admin/booking/history"
                className={`custom-button nav-link ${location.pathname === '/admin/booking/history' ? 'active' : ''}`}
            >
                🗓️ Bookings
            </Link>
            <Link
                to="/admin/users/transactions"
                className={`custom-button nav-link ${location.pathname === '/admin/users/transactions' ? 'active' : ''}`}
            >
                📋 Transactions
            </Link>
            <Link
                to="/admin/hotel-sequence/manage"
                className={`custom-button nav-link ${location.pathname === '/admin/hotel-sequence/manage' ? 'active' : ''}`}
            >
                🔢 Hotel Sequences
            </Link>
            <Link
                to="/admin/information"
                className={`custom-button nav-link ${location.pathname === '/admin/information' ? 'active' : ''}`}
            >
                ℹ️ Website Info
            </Link>
        </nav>
    );

    return (
        <div className="container-fluid p-0 d-flex min-vh-100">
            {loading && <LoadingOverlay />}

            {/* Desktop Sidebar */}
            {/* Desktop Sidebar */}
            {!isMobile && (
                <aside
                    className={`col-md-2 vh-100 position-fixed top-0 bottom-0 start-0 overflow-auto sidebar ${sidebarOpen ? 'd-block' : 'd-none'}`}
                >
                    <div className="d-flex justify-content-between align-items-center p-3 border-bottom sidebar-header">
                        <h1 className="h4 m-0 text-white">{sidebarOpen ? 'Dashboard' : 'D'}</h1>
                        <button
                            className="btn btn-sm btn-outline-light"
                            onClick={() => setSidebarOpen(false)}
                        >
                            ✖
                        </button>
                    </div>
                    <SidebarLinks />
                </aside>
            )}

            {/* Mobile Offcanvas Sidebar */}
            <Offcanvas
                show={sidebarOpen && isMobile}
                onHide={() => setSidebarOpen(false)}
                placement="start"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Dashboard</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <SidebarLinks />
                </Offcanvas.Body>
            </Offcanvas>

            {/* Main Content */}
            <main
                className={`col ${sidebarOpen && !isMobile ? 'offset-md-2 col-md-10' : 'col-12'} p-0`}
                style={{ marginLeft: sidebarOpen && !isMobile ? '250px' : '0' }}
            >
                <header className="navbar navbar-light bg-light p-2 d-flex justify-content-between align-items-center position-sticky top-0" style={{ zIndex: 999 }}>
                    {!sidebarOpen && !isMobile && (
                        <button
                            className="btn btn-outline-secondary sidebar-toggle-btn"
                            onClick={() => setSidebarOpen(true)}
                        >
                            ☰
                        </button>
                    )}
                    {isMobile && (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => setSidebarOpen(true)}
                        >
                            ☰
                        </button>
                    )}
                    <div className="ms-auto position-relative">
                        <div
                            className="btn btn-secondary rounded-circle d-flex justify-content-center align-items-center"
                            style={{ width: '40px', height: '40px', cursor: 'pointer' }}
                            onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                        >
                            {getInitials()}
                        </div>
                        {profileMenuOpen && (
                            <div className="dropdown-menu show position-absolute end-0 mt-2">
                                <Link to="/admin/profile" className="dropdown-item">Profile</Link>
                                <div
                                    className="dropdown-item"
                                    onClick={handleLogout}
                                >
                                    Logout
                                </div>
                            </div>
                        )}
                    </div>
                </header>
                <div className="p-3 min-vh-100">{children}</div>
            </main>
        </div>
    );
};

export default DashboardLayout;